import {
	useCallback,
	useContext,
	useEffect,
	useLayoutEffect,
	useState,
	useSyncExternalStore,
} from "react"
import { BackPriority, useBackButton } from "@/shared/lib/back-button"
import { OverlayHostContext } from "./host-context"
import {
	isTopmostOverlay,
	pushOverlay,
	subscribeOverlayStack,
} from "./overlay-stack"

type ChangeEventDetails = { reason: string; cancel: () => void }

/**
 * Общая механика Base UI-оверлеев (шит и центрированный диалог): фаза очереди
 * из хоста, gate на первый кадр ради enter-анимации и разбор причин закрытия.
 * `closeReasons` — единственное, чем примитивы расходятся; всё остальное
 * гасится `cancel()`.
 *
 * `onBack` is the system back handler of an overlay without `onClose`. Without
 * it, system back goes to the page below.
 */
export function useOverlayRoot<Details extends ChangeEventDetails>({
	open,
	onClose,
	onBack,
	closeReasons,
}: {
	open: boolean
	onClose?: (() => void) | undefined
	onBack?: (() => void) | undefined
	closeReasons: readonly Details["reason"][]
}) {
	const host = useContext(OverlayHostContext)

	// Изначально открытый рут Base UI считает уже смонтированным и не выставляет
	// data-starting-style — enter-анимация пропала бы. Первый кадр всегда закрыт.
	const [ready, setReady] = useState(false)
	useLayoutEffect(() => setReady(true), [])

	const dismissable = onClose != null
	const effectiveOpen = open && host.open && ready

	const [token] = useState(() => Symbol("overlay"))

	useEffect(() => {
		if (!effectiveOpen) return
		return pushOverlay(token)
	}, [effectiveOpen, token])

	const isTopmost = useSyncExternalStore(
		subscribeOverlayStack,
		useCallback(() => isTopmostOverlay(token), [token]),
	)

	// Системная «назад» закрывает оверлей только пока он открыт: на exit-фазе
	// обработчик снимается и «назад» проваливается на страницу.
	let backHandler: (() => void) | undefined
	if (effectiveOpen) backHandler = onClose ?? onBack
	useBackButton(backHandler, {
		priority: BackPriority.POPUP,
	})

	// Gesture dismissals reach the topmost overlay only. Base UI treats
	// independently mounted roots as unrelated, so a press inside a dialog
	// opened above a sheet counts as an outside press for that sheet and would
	// otherwise close both at once.
	const onOpenChange = (nextOpen: boolean, eventDetails: Details) => {
		if (nextOpen) return
		if (
			isTopmost &&
			dismissable &&
			closeReasons.includes(eventDetails.reason)
		) {
			onClose()
			return
		}
		eventDetails.cancel()
	}

	const onOpenChangeComplete = (nextOpen: boolean) => {
		if (nextOpen || host.open) return
		host.onExitComplete?.()
	}

	return { effectiveOpen, dismissable, onOpenChange, onOpenChangeComplete }
}
